
import { useEffect, useState } from 'react';

export default function Hero() {
  const [counts, setCounts] = useState({ views: 0, brands: 0, satisfaction: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const animateCount = (
      target: number,
      key: 'views' | 'brands' | 'satisfaction'
    ) => {
      let current = 0;
      const increment = target / 50;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounts((prev) => ({ ...prev, [key]: Math.floor(current) }));
      }, 30);
    };

    const timeout = setTimeout(() => {
      animateCount(500, 'views');
      animateCount(50, 'brands');
      animateCount(98, 'satisfaction');
    }, 500);

    // Cleanup timers on unmount
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden bg-black"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20male%20content%20creator%20filming%20product%20review%20video%20in%20modern%20studio%20setup%20with%20camera%20equipment%20ring%20lights%20and%20products%20on%20clean%20white%20background%20cinematic%20lighting%20high%20quality%20production&width=1200&height=1200&seq=hero-video-1&orientation=squarish"
          alt="James Moore UGC Creator Showreel"
          className="w-full h-full object-cover object-top scale-105 animate-slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20 pb-32">
        <div className="w-full max-w-7xl mx-auto text-center">
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="text-[#00D9FF] text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 sm:mb-6 font-medium">
              UGC Content Creator
            </p>

            <h1 className="mb-6 sm:mb-8">
              <div className="text-white font-black text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tight leading-none mb-1 sm:mb-2">
                JAMES
              </div>
              <div className="text-[#00D9FF] font-serif italic text-4xl sm:text-6xl md:text-7xl lg:text-8xl mb-1 sm:mb-2">
                Moore
              </div>
              <div className="text-white font-black text-3xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-none">
                BRAND STORYTELLER
              </div>
            </h1>

            <p className="text-gray-300 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-12 font-light px-4">
              Transforming products into compelling narratives that convert
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-16 px-4">
              <button
                onClick={() => scrollToSection('work')}
                className="w-full sm:w-auto bg-[#00D9FF] text-black px-8 py-4 rounded-full font-medium flex items-center justify-center gap-3 hover:bg-white transition-all duration-300 group cursor-pointer whitespace-nowrap"
              >
                View My Work
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
              </button>
              <button className="w-full sm:w-auto border-2 border-white/80 text-white px-8 py-4 rounded-full font-medium flex items-center justify-center gap-3 hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap">
                Download Media Kit
                <i className="ri-download-line"></i>
              </button>
            </div>
          </div>

          {/* Metrics Row - Inline on desktop, stacked on mobile */}
          <div
            className={`flex flex-row items-center justify-center gap-4 sm:gap-8 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 sm:px-8 py-3 sm:py-5 border border-white/20 text-center">
              <div className="text-[#00D9FF] text-2xl sm:text-3xl font-bold">
                {counts.views}K+
              </div>
              <div className="text-white/80 text-xs sm:text-sm font-light">
                Total Views
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 sm:px-8 py-3 sm:py-5 border border-white/20 text-center">
              <div className="text-[#00D9FF] text-2xl sm:text-3xl font-bold">
                {counts.brands}+
              </div>
              <div className="text-white/80 text-xs sm:text-sm font-light">
                Brands
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 sm:px-8 py-3 sm:py-5 border border-white/20 text-center">
              <div className="text-[#00D9FF] text-2xl sm:text-3xl font-bold">
                {counts.satisfaction}%
              </div>
              <div className="text-white/80 text-xs sm:text-sm font-light">
                Satisfaction
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-[2px] h-8 sm:h-12 bg-white/50 relative overflow-hidden">
          <div className="absolute top-0 w-full h-4 bg-[#00D9FF] animate-scroll-line"></div>
        </div>
        <p className="text-white/70 text-[10px] sm:text-xs tracking-wider uppercase">
          Scroll
        </p>
      </div>

      <style>{`
        @keyframes slow-zoom {
          0%, 100% { transform: scale(1.05); }
          50% { transform: scale(1.1); }
        }
        .animate-slow-zoom {
          animation: slow-zoom 20s ease-in-out infinite;
        }
        @keyframes scroll-line {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(300%); }
        }
        .animate-scroll-line {
          animation: scroll-line 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
